import { useEffect, useMemo, useState } from "react";
import {
    buildCompanyEventPayload,
    buildNewRulePrompt,
    buildRuleChangePrompt,
    collectRulePieceKeys,
    extractRuleChangeSummary,
    fetchRulePieceDefinitions,
    getRuleAgentDisplayText,
    RULE_DESCRIPTION_MAX_LENGTH
} from "./RuleTree/ruleAgentClient";
import {
    buildCompanyEventEntry,
    buildPreviousRuleEntry,
    buildRuleEntry,
    buildRulePieceEntry
} from "./ruleAgentContext";
import { translateOutcomeTreeData } from "./RuleTree/treeUtils";

export const RULE_WARNINGS_IDENTIFIER = "qi-rule-warnings";

const OPENING_FENCE = "```" + RULE_WARNINGS_IDENTIFIER + "\n";
const CLOSING_FENCE = "\n```";

function splitWarningBlocks(fullText) {
    let warnings = [];
    let text = fullText || "";

    while (true) {
        let start = text.indexOf(OPENING_FENCE);
        if (start === -1) break;

        let bodyStart = start + OPENING_FENCE.length;
        let end = text.indexOf(CLOSING_FENCE, bodyStart);
        if (end === -1) {
            text = text.slice(0, start);
            break;
        }

        try {
            let parsed = JSON.parse(text.slice(bodyStart, end));
            (parsed.warnings || []).forEach((warning) => warnings.push(warning));
        } catch (parseError) {
            console.warn("Failed to parse a " + RULE_WARNINGS_IDENTIFIER + " block:", parseError);
        }
        text = text.slice(0, start) + text.slice(end + CLOSING_FENCE.length);
    }

    return { warnings: warnings, text: text.trim() };
}

const ELLIPSIS = "\u2026";

function fitRuleDescription(summary) {
    if (!summary) return summary;
    let flattened = summary.replace(/\s+/g, " ").trim();
    if (flattened.length <= RULE_DESCRIPTION_MAX_LENGTH) return flattened;

    let budget = RULE_DESCRIPTION_MAX_LENGTH - ELLIPSIS.length;
    let cut = flattened.slice(0, budget);
    let lastSpace = cut.lastIndexOf(" ");
    if (lastSpace > budget - 40) cut = cut.slice(0, lastSpace);
    return cut.replace(/[\s.,;:—-]+$/, "") + ELLIPSIS;
}

export function readRuleReviewAnswer(fullText) {
    let { warnings, text } = splitWarningBlocks(fullText);
    return {
        warnings: warnings,
        text: getRuleAgentDisplayText(text),
        summary: fitRuleDescription(extractRuleChangeSummary(text))
    };
}

export function criticalWarningsOf(warnings) {
    return (warnings || []).filter((warning) => warning.severity === "critical");
}

export function useRuleReviewRequest({ enabled, treeData, ruleDefinition, userVariablesAvailable, agentContext, rulesContext, isNewRule, isRulePiece }) {
    const environment = ruleDefinition.environment;
    const companyKey = ruleDefinition.company_key;

    const ruleDataJson = useMemo(() => {
        if (!enabled) return null;
        let data = {
            tree_data: translateOutcomeTreeData(treeData),
            user_variables: userVariablesAvailable || [],
            name: ruleDefinition.name
        };
        if (isRulePiece) data.is_rule_piece = true;
        if (agentContext) data.agent_context = agentContext;
        return JSON.stringify(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, treeData, userVariablesAvailable, ruleDefinition.name, isRulePiece, agentContext]);

    const previousTreeDataJson = useMemo(
        () => (enabled && !isNewRule ? JSON.stringify(ruleDefinition.tree_data || []) : null),
        [enabled, isNewRule, ruleDefinition.tree_data]
    );

    const [rulePieceEntries, setRulePieceEntries] = useState(null);

    useEffect(() => {
        if (!ruleDataJson) {
            setRulePieceEntries(null);
            return undefined;
        }

        let keys = collectRulePieceKeys(JSON.parse(ruleDataJson).tree_data);
        if (previousTreeDataJson) {
            collectRulePieceKeys(JSON.parse(previousTreeDataJson)).forEach((key) => {
                if (!keys.includes(key)) keys.push(key);
            });
        }
        if (keys.length === 0) {
            setRulePieceEntries([]);
            return undefined;
        }

        let cancelled = false;
        setRulePieceEntries(null);
        fetchRulePieceDefinitions({ keys: keys, environment: environment, companyKey: companyKey })
            .then((definitions) => {
                if (cancelled) return;
                setRulePieceEntries(definitions.map(buildRulePieceEntry));
            });
        return () => {
            cancelled = true;
        };
    }, [ruleDataJson, previousTreeDataJson, environment, companyKey]);

    const chatContext = useMemo(() => {
        if (!enabled) return [];
        let payload = buildCompanyEventPayload(rulesContext);
        return payload ? [buildCompanyEventEntry(payload)] : [];
    }, [enabled, rulesContext]);

    const turnContext = useMemo(() => {
        if (!ruleDataJson || rulePieceEntries === null) return [];
        let entries = [buildRuleEntry(JSON.parse(ruleDataJson))];
        if (previousTreeDataJson) entries.push(buildPreviousRuleEntry(JSON.parse(previousTreeDataJson)));
        return entries.concat(rulePieceEntries);
    }, [ruleDataJson, previousTreeDataJson, rulePieceEntries]);

    const prompt = useMemo(() => {
        if (!ruleDataJson || rulePieceEntries === null) return null;
        let options = { isRulePiece: isRulePiece };
        return isNewRule ? buildNewRulePrompt(options) : buildRuleChangePrompt(options);
    }, [ruleDataJson, rulePieceEntries, isRulePiece, isNewRule]);

    return { chatContext: chatContext, turnContext: turnContext, prompt: prompt };
}
